import { Injectable, OnDestroy } from "@angular/core";
import { MatDialog, MatDialogConfig, MatDialogRef } from "@angular/material/dialog";
import { BreakpointObserver } from "@angular/cdk/layout";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { DsModalComponent } from "../components/ds-modal/ds-modal.component";
import { DsModalConfig } from "../components/types/modal-types";

/** Mobile breakpoint where modals dock to the bottom of the screen instead of centering. */
const MOBILE_BREAKPOINT = "(max-width: 767px)";

@Injectable({
  providedIn: "root",
})
export class DsModalService implements OnDestroy {
  private isMobile = false;
  private readonly destroy$ = new Subject<void>();

  constructor(private dialog: MatDialog, private breakpointObserver: BreakpointObserver) {
    this.breakpointObserver
      .observe(MOBILE_BREAKPOINT)
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        this.isMobile = result.matches;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  open(config: DsModalConfig): MatDialogRef<any> {
    // Standardized behavior: modals dock to the bottom on mobile and stay centered on desktop,
    // unless the caller explicitly sets `fixedBottom`.
    const fixedBottom = config.fixedBottom ?? this.isMobile;
    const defaultConfig: MatDialogConfig = {
      autoFocus: config.autoFocus ?? false,
      disableClose: config.disableClose ?? true,
      restoreFocus: config.restoreFocus ?? false,
      closeOnNavigation: config.closeOnNavigation ?? true,
      panelClass: [
        ...(config.innerComponent ? ["ds-modal", "full-screen"] : ["ds-modal"]),
        ...(fixedBottom ? ["ds-modal--fixed-bottom"] : []),
        ...(config.panelClass || []),
      ],
      maxWidth: fixedBottom ? "100vw" : config.maxWidth || "100%",
      ...(fixedBottom
        ? {
            width: "100%",
            maxHeight: "100vh",
            position: { bottom: "0", left: "0", right: "0" },
          }
        : config.width
        ? { width: config.width }
        : {}),
      data: {
        title: config.title,
        description: config.description,
        descriptionHtml: config.descriptionHtml ?? false,
        submitLabel: config.submitLabel,
        cancelLabel: config.cancelLabel,
        stateSubmit: config.stateSubmit,
        stateCancel: config.stateCancel,
        disabledSubmit: config.disabledSubmit ?? false,
        disabledCancel: config.disabledCancel ?? false,
        loadingSubmit: config.loadingSubmit ?? false,
        loadingCancel: config.loadingCancel ?? false,
        loadingTextSubmit: config.loadingTextSubmit ?? "Cargando",
        loadingTextCancel: config.loadingTextCancel ?? "Cargando",
        innerComponent: config.innerComponent || null,
        innerComponentData: config.data,
        onSubmit: config.onSubmit,
        onCancel: config.onCancel,
        cancelDsIcon: config.cancelDsIcon,
        submitDsIcon: config.submitDsIcon,
        titleDsIcon: config.titleDsIcon,
        fixedBottom,
      },
    };

    return this.dialog.open(DsModalComponent, defaultConfig);
  }
}
